'use client';

/**
 * MessagingSendBubble — minimalistic block for messaging-platform send
 * tool calls (telegram_send_text / telegram_send_photo /
 * telegram_send_document, and equivalents on other providers).
 *
 * The previous design wrapped the body in a chat-bubble (accent bg,
 * border, shadow, rounded-2xl, right-aligned, max-w-[85%]) on top of the
 * surrounding assistant message card — double-bubble, visually noisy.
 *
 * The current design renders the body flush as the agent's normal
 * markdown reply, prefixed by a single quiet meta line carrying the
 * platform icon, the recipient, and the delivery state. A 2px left rail
 * is the only structural cue.
 *
 * For media sends the bubble grows a media block above the optional
 * caption: an inline `<ImagePreview>` for photos and a `<DocumentChip>`
 * for documents. Both lean on existing components so capture, lightbox,
 * and download affordances stay consistent with regular chat content.
 *
 * `messageId` stays on the props for tests / future tooltips but is NOT
 * rendered.
 */

import { AlertCircle, CheckCheck } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { ProviderIcon } from '@/components/provider-icon';
import { Markdown } from '@/components/markdown';
import { Spinner } from '@/components/ui/spinner';
import { ImagePreview } from '@/components/content-previews';
import { DocumentChip } from '@/components/chat/document-chip';
import { cn } from '@/lib/utils';

export type MessagingSendState = 'sending' | 'sent' | 'error';

/**
 * Discriminated media payload. `contentId` is the canonical Robutler
 * UUID — when present we resolve it to `/api/content/<id>` so the same
 * bytes the agent sent to the platform are rendered locally. `url` is a
 * fallback for external https links (already-public assets).
 */
export type MessagingSendMedia =
  | {
      kind: 'photo';
      contentId?: string | null;
      url?: string | null;
      alt?: string | null;
    }
  | {
      kind: 'document';
      contentId?: string | null;
      url?: string | null;
      filename?: string | null;
      mimeType?: string | null;
    };

export type MessagingSendCategory = 'reply' | 'publish';

export interface MessagingSendBubbleProps {
  /** Platform slug — drives the icon (`telegram`, `slack`, etc.). */
  platform: string;
  /** Optional caption / message body. Rendered through the project Markdown component. */
  text?: string | null;
  /** Optional recipient display string (chat title, username, e164…). */
  recipient?: string | null;
  /**
   * Platform-side message id (Telegram `message_id`, etc.). Kept on the
   * props for callers / tests but intentionally NOT rendered — it's
   * debug noise that competes with the actual content.
   */
  messageId?: string | number | null;
  /** Delivery state, drives the meta line indicator. */
  state?: MessagingSendState;
  /** Optional error message to surface beneath the body on `state==='error'`. */
  errorMessage?: string | null;
  /**
   * Optional media payload. When present the bubble renders a photo
   * preview or document chip above the (also optional) caption. Absent
   * for plain text sends.
   */
  media?: MessagingSendMedia | null;
  /**
   * Whether this send is a private reply to a contact (`reply`) or a
   * public publish to a channel / feed (`publish`). Drives the meta
   * line copy ("to {recipient}" vs "published to {platform}") so
   * publishing tools don't read like 1:1 DMs.
   */
  category?: MessagingSendCategory;
  /**
   * Optional URL to the published artifact (e.g. Reddit post, Slack
   * permalink, Bluesky AT-URI). Surfaced as a small "view post" link
   * in the meta line when the send completed successfully and the
   * category is `publish`.
   */
  permalink?: string | null;
}

const PLATFORM_LABELS: Record<string, string> = {
  telegram: 'Telegram',
  slack: 'Slack',
  discord: 'Discord',
  whatsapp: 'WhatsApp',
  messenger: 'Messenger',
  instagram: 'Instagram',
  twilio: 'SMS',
  x: 'X',
  'google-chat': 'Google Chat',
  sendgrid: 'Email',
};

function StateIndicator({ state }: { state: MessagingSendState }) {
  if (state === 'sending') {
    return (
      <span className="inline-flex items-center gap-1">
        <Spinner size="xs" />
        <span>sending</span>
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="inline-flex items-center gap-1 text-red-500">
        <AlertCircle size={11} />
        <span>failed</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <CheckCheck size={11} className="text-blue-500" aria-hidden="true" />
      <span>sent</span>
    </span>
  );
}

function resolveMediaSrc(media: MessagingSendMedia): string | null {
  const id = media.contentId?.trim();
  if (id) return `/api/content/${id}`;
  const url = media.url?.trim();
  return url || null;
}

function MediaBlock({ media }: { media: MessagingSendMedia }) {
  if (media.kind === 'photo') {
    const src = resolveMediaSrc(media);
    if (!src) return null;
    return (
      <div className="my-1" data-testid="messaging-send-photo">
        <ImagePreview src={src} alt={media.alt ?? 'Sent photo'} className="max-w-xs" />
      </div>
    );
  }

  // document
  const filename = media.filename?.trim() || 'document';
  const mimeType = media.mimeType?.trim() || 'application/octet-stream';
  const contentId = media.contentId?.trim();
  if (contentId) {
    return (
      <div className="my-1" data-testid="messaging-send-document">
        <DocumentChip contentId={contentId} filename={filename} mimeType={mimeType} />
      </div>
    );
  }
  // External URL — DocumentChip is content-store-bound, so render a
  // minimal anchor instead. Keeps the chrome consistent with the rest of
  // the bubble (single quiet line, no card).
  const url = media.url?.trim();
  if (!url) return null;
  return (
    <div className="my-1" data-testid="messaging-send-document">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-sm underline decoration-dotted underline-offset-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
      >
        {filename}
      </a>
    </div>
  );
}

export function MessagingSendBubble({
  platform,
  text,
  recipient,
  state = 'sent',
  errorMessage,
  media,
  category = 'reply',
  permalink,
}: MessagingSendBubbleProps) {
  const t = useT();
  const platformLabel = PLATFORM_LABELS[platform] ?? platform;
  const recipientLabel = recipient?.trim() || `the ${platformLabel} contact`;
  const targetLabel =
    category === 'publish'
      ? recipient?.trim()
        ? `posted to ${recipientLabel}`
        : `published to ${platformLabel}`
      : `to ${recipientLabel}`;
  const body = (text ?? '').trim();
  const hasMedia = media != null;
  const hasBody = body.length > 0;
  const showPermalink = category === 'publish' && state === 'sent' && !!permalink;

  return (
    <div
      className={cn(
        'flex flex-col items-stretch gap-1 my-1 min-w-0',
        'border-l-2 border-[var(--border)] pl-3',
        state === 'error' && 'border-red-500/40',
      )}
      data-testid="messaging-send-bubble"
    >
      <div className="flex items-center gap-1.5 text-[10px] text-[var(--muted-foreground)]">
        <ProviderIcon slug={platform} size={11} className="opacity-70" />
        <span className="truncate">{targetLabel}</span>
        {showPermalink && (
          <a
            href={permalink as string}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-dotted underline-offset-2 hover:text-[var(--foreground)]"
            data-testid="messaging-send-permalink"
          >
            {t('sendBubble.viewPost')}
          </a>
        )}
        <span className="ml-auto shrink-0">
          <StateIndicator state={state} />
        </span>
      </div>
      {hasMedia && <MediaBlock media={media!} />}
      {hasBody ? (
        <div className="text-sm leading-snug min-w-0 break-words">
          <Markdown suppressLinkPreviews>{body}</Markdown>
        </div>
      ) : (
        // Empty placeholder is only meaningful for plain text sends. A
        // photo / document with no caption is a complete message on its
        // own — no need to scream "(empty message)" at the user.
        !hasMedia && (
          <div className="text-sm italic text-[var(--muted-foreground)]">(empty message)</div>
        )
      )}
      {state === 'error' && errorMessage && (
        <div className="text-[10px] text-red-500 break-words">{errorMessage}</div>
      )}
    </div>
  );
}

export default MessagingSendBubble;
